import React from 'react';

import { Button } from '@99designs/common/components/Button';
import { Icon } from '@99designs/common/components/Icon';
import { DesignerLevel, DesignerSource, PenaltyState } from '__generated__/types';
import { handlePromise } from 'common/utils/Bugsnag';
import { Color } from 'projects/consts';
import { trackAddedDesignerEvent, trackRemovedDesignerEvent } from 'projects/events';
import AppendStructuredBriefDesigners from 'projects/mutations/AppendStructuredBriefDesigners';
import RemoveStructuredBriefDesigners from 'projects/mutations/RemoveStructuredBriefDesigners';
import ColorComponent from './Color';

export interface SelectDesignerLabels {
    selected: string;
    notSelected: string;
}

interface Props {
    designer: {
        id: number;
        penaltyState: PenaltyState;
        displayName: string;
        profile: {
            designerLevel: DesignerLevel;
        };
    };
    position?: number;
    designerSource: DesignerSource;
    onAddDesigner?: () => void;
    selectDesignerLabels?: SelectDesignerLabels;
    totalAllowableDesigners: number;
    brief: {
        id: string;
        designers: {
            id: number;
        }[];
    };
    color?: Color;
}

const AddDesignerButton = ({
    designer,
    position,
    designerSource,
    onAddDesigner,
    selectDesignerLabels = {
        selected: __('Added'),
        notSelected: __('Add'),
    },
    color = Color.createBlue,
    brief,
    totalAllowableDesigners,
}: Props) => {
    return (
        <AppendStructuredBriefDesigners>
            {appendDesigners => (
                <RemoveStructuredBriefDesigners>
                    {removeDesigners => {
                        const isSuspended = designer.penaltyState === PenaltyState.SUSPENDED;
                        const isSelected = brief.designers.some(d => d.id == designer.id);
                        const shouldDisable =
                            brief.designers.length === totalAllowableDesigners || isSuspended;

                        return (
                            <ColorComponent color={color}>
                                <Button
                                    data-test-id="add-designer-button"
                                    isDisabled={shouldDisable && !isSelected}
                                    onClick={
                                        isSelected
                                            ? () => {
                                                  trackRemovedDesignerEvent({
                                                      id: brief.id,
                                                      relevant_ranking: position,
                                                      designer_id: designer.id,
                                                  });
                                                  handlePromise(
                                                      removeDesigners({
                                                          id: brief.id,
                                                          designerIds: [designer.id],
                                                      }),
                                                      'AddDesignerButton AddDesigners'
                                                  );
                                              }
                                            : () => {
                                                  trackAddedDesignerEvent({
                                                      id: brief.id,
                                                      relevant_ranking: position,
                                                      designer_id: designer.id,
                                                      designer_name: designer.displayName,
                                                      designer_level:
                                                          designer.profile.designerLevel,
                                                      designer_source: designerSource,
                                                  });
                                                  handlePromise(
                                                      appendDesigners({
                                                          id: brief.id,
                                                          designerIds: [designer.id],
                                                          source: designerSource,
                                                      }).then(() => {
                                                          if (onAddDesigner) {
                                                              onAddDesigner();
                                                          }
                                                      }),
                                                      'AddDesignerButton RemoveDesigners'
                                                  );
                                              }
                                    }
                                    variations={
                                        isSelected
                                            ? ['fluid', 'primary', 'chameleon']
                                            : ['fluid', 'secondary']
                                    }
                                    icon={isSelected ? <Icon type={'tick-3'} /> : undefined}
                                    iconAlignment="right"
                                >
                                    {isSuspended
                                        ? __('Designer suspended')
                                        : isSelected
                                        ? selectDesignerLabels.selected
                                        : selectDesignerLabels.notSelected}
                                </Button>
                            </ColorComponent>
                        );
                    }}
                </RemoveStructuredBriefDesigners>
            )}
        </AppendStructuredBriefDesigners>
    );
};

export default AddDesignerButton;
